import { useMemo, useState } from "react";
import { clock, distance } from "../lib/format";
import { passTime, type TripResult } from "../lib/trip";
import { ChurchCard } from "./ChurchCard";

const WINDOW_MS = 60 * 60 * 1000;

type Filter = "all" | "confession" | "adoration";

/** Every church along the route, in the order you reach it. */
export function ChurchList({ result }: { result: TripResult }) {
  const home = result.originTz;
  const [filter, setFilter] = useState<Filter>("all");

  const rows = useMemo(
    () =>
      result.churches.map((entry) => {
        const pass = passTime(result, entry);
        const events = result.events.get(entry.church.id) ?? [];
        const nearby = events.filter((event) => Math.abs(event.start - pass) <= WINDOW_MS || (event.end !== null && event.start <= pass && pass <= event.end));
        return { entry, pass, nearby };
      }),
    [result],
  );

  const shown = rows.filter(({ entry }) =>
    filter === "all" ? true : entry.church.services.some((service) => service.category === filter),
  );

  return (
    <section className="church-list">
      <div className="list-head">
        <p className="muted">
          {result.churches.length} churches within {distance(result.settings.corridorKm)} of the route, in the order you reach them.
        </p>
        <div className="segmented" role="group" aria-label="Show">
          {(["all", "confession", "adoration"] as Filter[]).map((value) => (
            <button key={value} type="button" aria-pressed={filter === value} onClick={() => setFilter(value)}>
              {value === "all" ? "All" : value === "confession" ? "Confession" : "Adoration"}
            </button>
          ))}
        </div>
      </div>
      {result.narrowed.length > 0 && (
        <p className="fine">
          Around busy cities the search hugged the route more tightly (down to {distance(Math.min(...result.narrowed.map((n) => n.halfWidthKm)))}) to keep
          requests reasonable.
        </p>
      )}
      <ol className="cards">
        {shown.map(({ entry, pass, nearby }) => {
          const tz = result.timeZones.get(entry.church.id) ?? home;
          return (
            <li key={entry.church.id} id={`church-${entry.church.id}`}>
              <p className="pass">
                Pass around <b>{clock(pass, tz, home)}</b> · {distance(entry.join.alongKm)} along · {distance(entry.join.offKm)} off route
              </p>
              <ChurchCard church={entry.church} timeZone={tz} homeZone={home} nearby={nearby} />
            </li>
          );
        })}
      </ol>
    </section>
  );
}
